import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";

const HOUSE_MODELS = ["SULUFA", "KAKO", "BOWUTE", "ABULU"];

export default function ClientForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        address: "",
        city: "",
        purchase_type: "terrain",
        contract_type: "bon_reservation",
        house_model: "",
        plot_reference: "",
        total_amount: 0,
        notes: "",
    });

    useEffect(() => {
        if (!isEdit) return;
        api.get(`/clients/${id}`).then((r) => {
            const c = r.data;
            setForm({
                first_name: c.first_name || "",
                last_name: c.last_name || "",
                phone: c.phone || "",
                email: c.email || "",
                address: c.address || "",
                city: c.city || "",
                purchase_type: c.purchase_type,
                contract_type: c.contract_type,
                house_model: c.house_model || "",
                plot_reference: c.plot_reference || "",
                total_amount: c.total_amount,
                notes: c.notes || "",
            });
        });
    }, [id, isEdit]);

    function update(field, value) {
        setForm((f) => ({ ...f, [field]: value }));
    }

    async function submit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...form,
                total_amount: Number(form.total_amount),
                email: form.email || null,
                house_model: form.purchase_type === "terrain_maison" ? form.house_model : null,
            };
            if (isEdit) {
                await api.put(`/clients/${id}`, payload);
                toast.success("Client mis à jour");
                navigate(`/clients/${id}`);
            } else {
                const { data } = await api.post("/clients", payload);
                toast.success("Client créé");
                navigate(`/clients/${data.id}`);
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Layout>
            <div className="mb-8">
                <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-2">
                    {isEdit ? "Modifier" : "Nouveau"}
                </p>
                <h1 className="font-display text-4xl font-bold text-ink">
                    {isEdit ? "Modifier le client" : "Nouveau client"}
                </h1>
            </div>

            <form onSubmit={submit} className="space-y-6 max-w-4xl" data-testid="client-form">
                <div className="bento-card">
                    <h3 className="font-display text-lg font-bold text-ink mb-4">Données personnelles</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Prénom">
                            <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required data-testid="input-first-name" />
                        </Field>
                        <Field label="Nom">
                            <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} required data-testid="input-last-name" />
                        </Field>
                        <Field label="Téléphone (format international, ex: +22890000000)">
                            <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} required data-testid="input-phone" />
                        </Field>
                        <Field label="Email (optionnel)">
                            <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="input-email" />
                        </Field>
                        <Field label="Adresse" full>
                            <Input value={form.address} onChange={(e) => update("address", e.target.value)} data-testid="input-address" />
                        </Field>
                    </div>
                </div>

                <div className="bento-card">
                    <h3 className="font-display text-lg font-bold text-ink mb-4">Achat</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Cité">
                            <Input value={form.city} onChange={(e) => update("city", e.target.value)} required placeholder="Ex: Cité Avenir" data-testid="input-city" />
                        </Field>
                        <Field label="Référence parcelle">
                            <Input value={form.plot_reference} onChange={(e) => update("plot_reference", e.target.value)} placeholder="Ex: Lot 12 / Bloc A" data-testid="input-plot" />
                        </Field>
                        <Field label="Nature de l'achat">
                            <Select value={form.purchase_type} onValueChange={(v) => update("purchase_type", v)}>
                                <SelectTrigger data-testid="select-purchase-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="terrain">Terrain seul</SelectItem>
                                    <SelectItem value="terrain_maison">Terrain + Maison</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Type de contrat">
                            <Select value={form.contract_type} onValueChange={(v) => update("contract_type", v)}>
                                <SelectTrigger data-testid="select-contract-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="bon_reservation">Bon de réservation</SelectItem>
                                    <SelectItem value="contrat_signe">Contrat signé</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        {form.purchase_type === "terrain_maison" && (
                            <Field label="Modèle de maison">
                                <Select value={form.house_model} onValueChange={(v) => update("house_model", v)}>
                                    <SelectTrigger data-testid="select-house-model"><SelectValue placeholder="Choisir un modèle" /></SelectTrigger>
                                    <SelectContent>
                                        {HOUSE_MODELS.map((m) => (
                                            <SelectItem key={m} value={m}>{m}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                        )}
                        <Field label="Montant total (USD)">
                            <Input type="number" min="0" step="0.01" value={form.total_amount} onChange={(e) => update("total_amount", e.target.value)} required data-testid="input-total-amount" />
                        </Field>
                        <Field label="Notes" full>
                            <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={3} data-testid="input-notes" />
                        </Field>
                    </div>
                </div>

                <div className="flex gap-3">
                    <Button
                        type="submit"
                        disabled={loading}
                        className="bg-terracotta hover:bg-terracotta-dark text-white px-6 py-5 rounded-md"
                        data-testid="submit-client-button"
                    >
                        {loading ? "Enregistrement…" : isEdit ? "Mettre à jour" : "Créer le client"}
                    </Button>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => navigate(-1)}
                        className="bg-sand hover:bg-sand/70 text-ink px-6 py-5 rounded-md"
                        data-testid="cancel-button"
                    >
                        Annuler
                    </Button>
                </div>
            </form>
        </Layout>
    );
}

function Field({ label, children, full }) {
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <Label className="text-xs uppercase tracking-widest text-ink-soft">{label}</Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
